import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getEstimatedBloodLossRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface EstimatedBloodLossRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function EstimatedBloodLossRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
}: EstimatedBloodLossRowProps) {
  const { estimated, quantitative, hasAnswers, hasNotes, notes } = useMemo(
    () => getEstimatedBloodLossRowValues(observation),
    [observation],
  );

  const bloodLossItems = useMemo(
    () => [
      { label: "Estimated Blood Loss:", value: estimated },
      { label: "Quantitative Blood Loss:", value: quantitative },
    ],
    [estimated, quantitative],
  );

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Estimated Blood Loss:</TextWrapper>
            <List fz="sm" withPadding>
              {bloodLossItems.map(
                (item, index) =>
                  item.value && (
                    <List.Item key={index}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Estimated Blood Loss Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={EstimatedBloodLossRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
